import {
  AgropilotPermissionManagerService,
  API_URL,
  AuthService,
  ErrorInterceptor,
  LoggerService,
  MonitoringService,
  provideTranslations,
  SharedConstantService,
  SharedPermissionService,
  SharedUserService,
  ToastService,
  VersionService,
  WithCredentialsInterceptor,
} from '@agroone-front/shared'
import { UserDto } from '@agroone/entities'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http'
import {
  ApplicationConfig,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { provideRouter } from '@angular/router'
import { provideServiceWorker } from '@angular/service-worker'
import { environment } from '@front-app-environments/environment'
import { TranslateService } from '@ngx-translate/core'
import { firstValueFrom } from 'rxjs'
import { appRoutes } from './app.routes'
import { ErrorService } from './core/error/services/error.service'
import { NetworkingService } from './core/networking/services/networking.service'
import { CroptypeService } from './core/services/croptype/croptype.service'
import { FilterService } from './core/services/filter/filter.service'
import { PermissionService } from './core/services/permissions/permission.service'
import { RegionService } from './core/services/region/region.service'
import { SettingsService } from './core/services/settings/settings.service'
import { TechniciansService } from './core/services/technicians/technicians.service'
import { UserService } from './core/services/user/user.service'
import { TranslateLocalIdFactory } from './core/translate/classes/translate-local-id-factory'
import { PriorityService } from './features/services/priority.service'
import { OfferService } from './shared/v2/crop/services/offer.service'
import { TranslationsLoader } from './translations.loader'

export const init =
  (
    userService: SharedUserService,
    filterService: FilterService,
    regionService: RegionService,
    croptypeService: CroptypeService,
    settingsService: SettingsService,
    constantService: SharedConstantService,
    loggerService: LoggerService,
    authService: AuthService,
    priorityService: PriorityService,
  ) =>
  async () => {
    // todo: remove the initialization lines below after migrating them to libs/
    loggerService.init(environment.logLevel, environment.serverLogLevel)
    priorityService.init()
    authService.init(environment.cognitoDomain, environment.cognitoClientId, environment.apiUrl)

    try {
      const user: UserDto = await firstValueFrom(userService.init())

      // Init constant
      await firstValueFrom(constantService.load(user))

      // Init filter
      filterService.init(user.regionId, user.regionName)

      // Init region
      await firstValueFrom(regionService.init())

      // Init croptypes
      await firstValueFrom(croptypeService.init())

      // Init Settings
      await settingsService.init()
    } catch (e) {
      console.error('App Initialisation failed due to', e)
    }
  }

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(appRoutes),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // Previously brought in by CoreModule.
    NetworkingService,
    ErrorService,
    provideTranslations(TranslationsLoader),
    provideAnimationsAsync(),
    PriorityService,
    provideAppInitializer(() => {
      const initializerFn = init(
        inject(SharedUserService),
        inject(FilterService),
        inject(RegionService),
        inject(CroptypeService),
        inject(SettingsService),
        inject(SharedConstantService),
        inject(LoggerService),
        inject(AuthService),
        inject(PriorityService),
      )
      return initializerFn()
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WithCredentialsInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useFactory: TranslateLocalIdFactory,
      deps: [TranslateService],
    },
    OfferService,
    {
      provide: API_URL,
      useValue: environment.apiUrl,
    },
    ToastService,
    LoggerService,
    AuthService,
    VersionService,
    TechniciansService,
    MonitoringService,
    {
      provide: SharedPermissionService,
      useClass: PermissionService,
    },
    {
      provide: SharedUserService,
      useClass: UserService,
    },
    AgropilotPermissionManagerService,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
}
